/**
 * Organization reporting period mode helpers (RCAP-02).
 */

import { DEFAULT_DASHBOARD_TIME_RANGE } from './dashboardScope';

export const REPORTING_PERIOD_MODES = {
    WEEK: 'week',
    MONTH: 'month',
    PROJECT_TO_DATE: 'project_to_date',
};

export const REPORTING_PERIOD_MODE_OPTIONS = [
    { value: REPORTING_PERIOD_MODES.WEEK, label: 'Current week to date' },
    { value: REPORTING_PERIOD_MODES.MONTH, label: 'Calendar month to date' },
    { value: REPORTING_PERIOD_MODES.PROJECT_TO_DATE, label: 'Full project to date' },
];

export const REPORTING_PERIOD_MODE_LABELS = Object.fromEntries(
    REPORTING_PERIOD_MODE_OPTIONS.map((option) => [option.value, option.label])
);

/**
 * Map org default period mode to dashboard scope time_range key.
 *
 * @param {string|null|undefined} mode - week | month | project_to_date
 * @returns {string} Dashboard scope timeRange (7d, 30d, 90d, …)
 */
export const reportingPeriodModeToDashboardTimeRange = (mode) => {
    switch (mode) {
        case REPORTING_PERIOD_MODES.WEEK:
            return '7d';
        case REPORTING_PERIOD_MODES.MONTH:
            return '30d';
        case REPORTING_PERIOD_MODES.PROJECT_TO_DATE:
            return '90d';
        default:
            return DEFAULT_DASHBOARD_TIME_RANGE;
    }
};

/**
 * @param {string|null|undefined} mode
 * @returns {string}
 */
export const getReportingPeriodModeLabel = (mode) => (
    REPORTING_PERIOD_MODE_LABELS[mode] || REPORTING_PERIOD_MODE_LABELS.month
);

/**
 * Resolve a mode label only when the mode is known — never invent one.
 *
 * @param {string|null|undefined} mode
 * @returns {string|null}
 */
export const resolveReportingPeriodModeLabel = (mode) => (
    mode && REPORTING_PERIOD_MODE_LABELS[mode] ? REPORTING_PERIOD_MODE_LABELS[mode] : null
);

/**
 * Format an inclusive ISO date range with a consistent em dash.
 *
 * @param {{ startDate?: string|null, endDate?: string|null,
 *   start_date?: string|null, end_date?: string|null }} [period]
 * @returns {string}
 */
export const formatReportingPeriodRange = (period = {}) => {
    const safePeriod = period || {};
    const startDate = safePeriod.startDate || safePeriod.start_date || null;
    const endDate = safePeriod.endDate || safePeriod.end_date || null;
    if (!startDate || !endDate) {
        return '';
    }
    return `${startDate} — ${endDate}`;
};

/**
 * Human-readable period range with optional server-provided mode suffix.
 *
 * @param {{ startDate?: string|null, endDate?: string|null,
 *   start_date?: string|null, end_date?: string|null, mode?: string|null }} [period]
 * @returns {string}
 */
export const formatReportingPeriodViewLabel = (period = {}) => {
    const range = formatReportingPeriodRange(period);
    if (!range) {
        return '';
    }
    const modeLabel = resolveReportingPeriodModeLabel(period.mode);
    return modeLabel ? `${range} (${modeLabel})` : range;
};

/**
 * Chip / caption label for the active reporting period.
 *
 * @param {{ startDate?: string|null, endDate?: string|null,
 *   start_date?: string|null, end_date?: string|null, mode?: string|null }} [period]
 * @param {{ prefix?: string }} [options]
 * @returns {string}
 */
export const formatReportingPeriodChipLabel = (period = {}, options = {}) => {
    const viewLabel = formatReportingPeriodViewLabel(period);
    if (!viewLabel) {
        return '';
    }
    const prefix = options.prefix === undefined ? 'Viewing' : options.prefix;
    return prefix ? `${prefix}: ${viewLabel}` : viewLabel;
};

/**
 * Queue scope kinds for as-of / through labels (Sprint 1 P0#7).
 */
export const QUEUE_SCOPE_KINDS = {
    APPROVED_ACTUALS : 'approved_actuals',
    OPEN_WIP         : 'open_wip',
    AR_AGING         : 'ar_aging',
    SUBMITTED        : 'submitted_awaiting',
    FUTURE_CAPACITY  : 'future_capacity',
};

const QUEUE_SCOPE_PREFIX = {
    [QUEUE_SCOPE_KINDS.APPROVED_ACTUALS]: 'Approved actuals through',
    [QUEUE_SCOPE_KINDS.OPEN_WIP]        : 'Open WIP as of',
    [QUEUE_SCOPE_KINDS.AR_AGING]        : 'AR aging as of',
    [QUEUE_SCOPE_KINDS.SUBMITTED]       : 'Submitted awaiting approval through',
    [QUEUE_SCOPE_KINDS.FUTURE_CAPACITY] : 'Future capacity window',
};

/**
 * Make time/data scope unmistakable on a queue surface.
 *
 * "Through" scopes use the full range; "as of" scopes prefer the period end date.
 *
 * @param {string} scopeKind - One of QUEUE_SCOPE_KINDS.
 * @param {{ startDate?: string|null, endDate?: string|null,
 *   start_date?: string|null, end_date?: string|null, mode?: string|null }} [period]
 * @returns {string}
 */
export const formatQueueScopeLabel = (scopeKind, period = {}) => {
    const prefix = QUEUE_SCOPE_PREFIX[scopeKind];
    if (!prefix) {
        return formatReportingPeriodChipLabel(period, { prefix: 'Period' });
    }

    const safePeriod = period || {};
    const startDate = safePeriod.startDate || safePeriod.start_date || null;
    const endDate = safePeriod.endDate || safePeriod.end_date || null;
    if (!startDate || !endDate) {
        return '';
    }

    const asOfScopes = new Set([
        QUEUE_SCOPE_KINDS.OPEN_WIP,
        QUEUE_SCOPE_KINDS.AR_AGING,
    ]);
    const modeLabel = resolveReportingPeriodModeLabel(safePeriod.mode);
    const when = asOfScopes.has(scopeKind) ? endDate : `${startDate} — ${endDate}`;
    return modeLabel ? `${prefix} ${when} (${modeLabel})` : `${prefix} ${when}`;
};

/**
 * Label a week-grain view that is derived from the selected reporting period.
 *
 * @param {string|null|undefined} weekStart - ISO week start (Monday).
 * @param {string|null|undefined} weekEnd - ISO week end (Sunday).
 * @returns {string}
 */
export const formatDerivedWeekPeriodLabel = (weekStart, weekEnd) => {
    if (!weekStart || !weekEnd) {
        return '';
    }
    return `Week ${weekStart} — ${weekEnd} (from selected period)`;
};

/**
 * Format a Date as local YYYY-MM-DD (noon-safe for DST).
 *
 * @param {Date} value
 * @returns {string}
 */
const toIsoDateLocal = (value) => {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Parse YYYY-MM-DD as a local calendar date at noon.
 *
 * @param {string} isoDate
 * @returns {Date|null}
 */
const parseIsoDateLocal = (isoDate) => {
    if (!isoDate || typeof isoDate !== 'string') {
        return null;
    }
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate.trim());
    if (!match) {
        return null;
    }
    const date = new Date(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]),
        12,
        0,
        0
    );
    return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Client bounds matching backend ``_default_period_bounds`` (without project clip
 * when no project dates are supplied).
 *
 * @param {string} periodMode - week | month | project_to_date
 * @param {{
 *   today?: string,
 *   projectStartDate?: string|null,
 *   projectEndDate?: string|null,
 * }} [options]
 * @returns {{ start_date: string, end_date: string }|null}
 */
export const resolveSemanticPeriodBounds = (periodMode, options = {}) => {
    const todayIso = options.today || toIsoDateLocal(new Date());
    const today = parseIsoDateLocal(todayIso);
    if (!today) {
        return null;
    }

    const projectStart = parseIsoDateLocal(options.projectStartDate) || today;
    const projectEnd = parseIsoDateLocal(options.projectEndDate) || today;

    let periodStart;
    let periodEnd = today < projectEnd ? today : projectEnd;

    if (periodMode === REPORTING_PERIOD_MODES.PROJECT_TO_DATE) {
        if (!options.projectStartDate) {
            // Portfolio-level project_to_date cannot invent a start without a project.
            return null;
        }
        periodStart = projectStart;
        periodEnd = today < projectEnd ? today : projectEnd;
    } else if (periodMode === REPORTING_PERIOD_MODES.WEEK) {
        const weekStart = new Date(today);
        const day = weekStart.getDay();
        const diffToMonday = day === 0 ? -6 : 1 - day;
        weekStart.setDate(weekStart.getDate() + diffToMonday);
        periodStart = weekStart;
        periodEnd = today < projectEnd ? today : projectEnd;
    } else if (periodMode === REPORTING_PERIOD_MODES.MONTH) {
        periodStart = new Date(today.getFullYear(), today.getMonth(), 1, 12, 0, 0);
        periodEnd = today < projectEnd ? today : projectEnd;
    } else {
        return null;
    }

    // Clip to project lifetime when project dates are known (parity with backend).
    if (options.projectStartDate || options.projectEndDate) {
        if (periodStart < projectStart) {
            periodStart = projectStart;
        }
        if (periodEnd > projectEnd) {
            periodEnd = projectEnd;
        }
    }

    if (periodEnd < periodStart) {
        periodEnd = periodStart;
    }

    return {
        start_date: toIsoDateLocal(periodStart),
        end_date: toIsoDateLocal(periodEnd),
    };
};

/**
 * Resolve Project Ops reporting period for API calls and display.
 *
 * Precedence:
 * 1. Explicit start+end dates → date params only (never invent a mode).
 * 2. Semantic periodMode → pass period_mode to mode-aware endpoints; derive
 *    client bounds (matching backend) for date-only endpoints / display.
 * 3. Otherwise empty — never fall through to a silent 90d window.
 *
 * @param {{
 *   startDate?: string|null,
 *   endDate?: string|null,
 *   periodMode?: string|null,
 *   projectStartDate?: string|null,
 *   projectEndDate?: string|null,
 *   today?: string,
 * }} [period]
 * @returns {{
 *   startDate: string|null,
 *   endDate: string|null,
 *   periodMode: string|null,
 *   usePeriodModeOnly: boolean,
 *   apiParams: Record<string, string>,
 *   dateParams: Record<string, string>,
 * }}
 */
export const resolveReportingPeriodForApi = (period = {}) => {
    const startDate = period.startDate || period.start_date || null;
    const endDate = period.endDate || period.end_date || null;
    const periodMode = period.periodMode || period.mode || null;

    if (startDate && endDate) {
        return {
            startDate,
            endDate,
            periodMode: null,
            usePeriodModeOnly: false,
            apiParams: { start_date: startDate, end_date: endDate },
            dateParams: { start_date: startDate, end_date: endDate },
        };
    }

    if (periodMode && REPORTING_PERIOD_MODE_LABELS[periodMode]) {
        const bounds = resolveSemanticPeriodBounds(periodMode, {
            today: period.today,
            projectStartDate: period.projectStartDate || period.project_start_date,
            projectEndDate: period.projectEndDate || period.project_end_date,
        });
        return {
            startDate: bounds?.start_date || null,
            endDate: bounds?.end_date || null,
            periodMode,
            usePeriodModeOnly: true,
            // Mode-aware endpoints get period_mode only (server resolves / clips).
            apiParams: { period_mode: periodMode },
            // Date-only endpoints get derived bounds when available.
            dateParams: bounds
                ? { start_date: bounds.start_date, end_date: bounds.end_date }
                : {},
        };
    }

    return {
        startDate: null,
        endDate: null,
        periodMode: null,
        usePeriodModeOnly: false,
        apiParams: {},
        dateParams: {},
    };
};
